import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { toHongKongTime } from "../../dateUtils";
import Avatar from "../user/Avatar";
dayjs.extend(relativeTime);

const getReplies = (comments, parentId) =>
  comments.filter((c) => c.parent_id === parentId);
// Emoji picker (可用 emoji-picker-react 或其他)
// import EmojiPicker from "emoji-picker-react";

const MAX_LENGTH = 500;

function AdComments({ adId, adOwnerId, currentUser, token }) {
  const { t } = useTranslation();
  const [comments, setComments] = useState([]);
  const [input, setInput] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [replyTo, setReplyTo] = useState(null); // {commentId, value, isPrivate}
  const [likeLoading, setLikeLoading] = useState({});
  const [reportLoading, setReportLoading] = useState({});
  const inputRef = useRef();

  // 加載留言
  const fetchComments = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/ad_comments.php`,
        {
          params: { ad_id: adId, sort: "desc" },
        },
      );
      setComments(res.data.data || []);
    } catch (e) {
      setError(t("commentsLoadFailed"));
    }
    setIsLoading(false);
  }, [adId, t]);

  useEffect(() => {
    fetchComments();
    // 可加輪詢或 WebSocket
  }, [fetchComments]);

  // 發送留言
  const handleSend = async () => {
    if (!input.trim() || input.length > MAX_LENGTH) return;
    setIsLoading(true);
    setError("");
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/ad_comments.php`,
        {
          ad_id: adId,
          content: input,
          parent_id: null,
          is_private: isPrivate ? 1 : 0,
          MsgContent: MsgContent(
            currentUser?.username || "匿名",
            input.slice(0, 30),
          ),
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      setInput("");
      setIsPrivate(false);
      fetchComments();
      toast.success(t("commentSendSuccess"));
    } catch (e) {
      setError(e.message);
      toast.error(
        t("commentSendFailed") + (e && e.message ? ": " + e.message : ""),
      );
    }
    setIsLoading(false);
  };

  // 定義多語言通知內容
  const MsgContent = (name, shortContent = "") => {
    return t("newCommentNotification", { name, shortContent });
  };

  // 發送回覆
  const handleReplySend = async (parentId, value, isPrivate = 0) => {
    if (!value.trim() || value.length > MAX_LENGTH) return;
    setIsLoading(true);
    setError("");
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/ad_comments.php`,
        {
          ad_id: adId,
          content: value,
          parent_id: parentId,
          is_private: isPrivate,
          MsgContent: MsgContent(
            currentUser?.username || "匿名",
            value.slice(0, 30),
          ),
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      setReplyTo(null);
      fetchComments();
      toast.success(t("replySendSuccess"));
    } catch (e) {
      setError(e.message);
      toast.error(
        t("replySendFailed") + (e && e.message ? ": " + e.message : ""),
      );
    }
    setIsLoading(false);
  };

  // Like
  const handleLike = async (commentId) => {
    if (!token) {
      toast.error(t("loginRequired", { defaultValue: "請先登入" }));
      return;
    }
    setLikeLoading((l) => ({ ...l, [commentId]: true }));
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/ad_comments/like.php`,
        { id: commentId },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      fetchComments();
      toast.success(t("likeSuccess"));
    } catch (e) {
      setError(e.message);
      toast.error(t("likeFailed") + (e && e.message ? ": " + e.message : ""));
    }
    setLikeLoading((l) => ({ ...l, [commentId]: false }));
  };

  // Report
  const handleReport = async (commentId) => {
    if (!token) {
      toast.error(t("loginRequired", { defaultValue: "請先登入" }));
      return;
    }
    setReportLoading((l) => ({ ...l, [commentId]: true }));
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/ad_comments/report.php`,
        { id: commentId },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      fetchComments();
    } catch (e) {
      setError(e.message);
    }
    setReportLoading((l) => ({ ...l, [commentId]: false }));
  };

  // 處理 emoji 點擊
  // const handleEmoji = (emoji) => setInput(input + emoji.emoji);

  // 僅顯示 parent_id 為 null 的主留言
  const mainComments = comments.filter((c) => !c.parent_id);

  return (
    <div className="ad-comments  mt-2 pt-2">
      {isLoading && <div>{t("loading")}</div>}
      {error && <div className="text-red-500">{error}</div>}
      {token ? (
        <div className="text-sm text-gray-600 mb-3">
          <div className="comment-input flex flex-col gap-2 items-end">
            <div className="flex flex-col w-full">
              <textarea
                ref={inputRef}
                className="flex-1 border rounded p-2"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                maxLength={MAX_LENGTH}
                rows={2}
                placeholder={t("commentPlaceholder")}
              />
            </div>
            <div className="w-full flex flex-wrap items-center justify-end gap-2">
              <div className="text-xs text-gray-400  text-left">
                {input.length}/{MAX_LENGTH}
              </div>
              <label className="flex items-center text-xs mb-1 select-none  ">
                <input
                  type="checkbox"
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                  //AD user 不可PM
                  hidden={adOwnerId === currentUser.id}
                  className="mr-1"
                />
                {adOwnerId !== currentUser.id ? t("pmCheckbox") : null}
              </label>
              <button
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded"
                onClick={handleSend}
                disabled={
                  !input.trim() || input.length > MAX_LENGTH || isLoading
                }
              >
                {t("send")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="comments-list space-y-2 pt-0-2 mb-2">
        {mainComments.map((c) => (
          <div
            key={c.id}
            className={`flex items-start gap-2 p-2 rounded-md ${
              c.user_id === adOwnerId
                ? "bg-orange-50 border-l-4 border-orange-400"
                : "bg-gray-50"
            }`}
          >
            <Avatar
              userId={c.user_id}
              height={20}
              width={20}
              imageUrl={c.profilePicture}
              name={c.username}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold">
                  {c.is_anonymous
                    ? t("anonymous") || "匿名"
                    : c.username || t("user") || "用戶"}
                </span>
                {c.user_id === adOwnerId && (
                  <span className="text-xs bg-orange-200 text-orange-700 px-2 py-0.5 rounded">
                    {t("sellerReply")}
                  </span>
                )}
                {c.is_private === 1 && (
                  <span className="text-xs bg-blue-200 text-blue-700 px-2 py-0.5 rounded ml-1">
                    {t("pm")}
                  </span>
                )}
                <span className="text-xs text-gray-400">
                  {/* 顯示香港時間 */}
                  {toHongKongTime(c.created_at)}
                </span>
              </div>
              <div className="whitespace-pre-line break-words mb-1">
                {c.is_private === 1 &&
                !(
                  currentUser &&
                  (currentUser.id === adOwnerId || currentUser.id === c.user_id)
                )
                  ? t("pmHidden")
                  : c.content}
              </div>
              <div className="flex gap-3 items-center text-xs text-gray-500 mt-1">
                {currentUser &&
                  (currentUser.id === adOwnerId ||
                    currentUser.id === c.user_id ||
                    !c.isPrivate) && (
                    <button
                      className="hover:underline"
                      onClick={() => {
                        if (!token) {
                          toast.error(
                            t("loginRequired", { defaultValue: "請先登入" }),
                          );
                          return;
                        }

                        setReplyTo({
                          commentId: c.id,
                          value: "",
                          isPrivate: c.is_private,
                        });
                      }}
                      disabled={!token || isLoading}
                    >
                      {t("comment")}
                    </button>
                  )}
                <button
                  className={`flex items-center gap-1 ${
                    c.liked ? "text-orange-500" : ""
                  }`}
                  onClick={() => handleLike(c.id)}
                  disabled={!token || likeLoading[c.id] || isLoading}
                  title={c.liked ? t("liked") : t("like")}
                >
                  👍 <span>{c.like_count || 0}</span>
                </button>
                <button
                  className={`flex items-center gap-1 ${
                    c.reported ? "text-red-500" : ""
                  }`}
                  onClick={() => handleReport(c.id)}
                  disabled={
                    !token || reportLoading[c.id] || c.reported || isLoading
                  }
                  title={c.reported ? t("reported") : t("report")}
                >
                  🚩
                  <span>{c.reported ? t("reported") : t("report")}</span>
                </button>
              </div>
              {/* 回覆輸入框 */}
              {replyTo &&
                replyTo.commentId === c.id &&
                (token ? (
                  <div className="mt-2 flex flex-col gap-2 items-end">
                    <div className="w-full">
                      <textarea
                        className="w-full border rounded text-sm"
                        value={replyTo.value}
                        onChange={(e) =>
                          setReplyTo({ ...replyTo, value: e.target.value })
                        }
                        maxLength={MAX_LENGTH}
                        rows={2}
                        placeholder={t("commentPlaceholder")}
                      />
                    </div>
                    <div className="w-full flex flex-wrap items-center justify-end gap-2">
                      <div className="text-xs text-blue-600 mr-2">
                        <label className="flex items-center text-xs mb-1 select-none  ">
                          <input
                            type="checkbox"
                            checked={replyTo.isPrivate}
                            onChange={(e) =>
                              setReplyTo({
                                ...replyTo,
                                isPrivate: e.target.checked,
                              })
                            }
                          />
                          {t("pm")}
                        </label>
                      </div>

                      <button
                        className="bg-orange-400 hover:bg-orange-500 text-white px-3 py-1 rounded text-sm"
                        onClick={() =>
                          handleReplySend(
                            c.id,
                            replyTo.value,
                            replyTo.isPrivate,
                          )
                        }
                        disabled={
                          !replyTo.value.trim() ||
                          replyTo.value.length > MAX_LENGTH ||
                          isLoading
                        }
                      >
                        {t("send")}
                      </button>
                      <button
                        className="text-gray-400 hover:text-gray-600 text-xs"
                        onClick={() => setReplyTo(null)}
                      >
                        {t("cancel") || "取消"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 text-sm text-gray-500">
                    {t("loginRequired", { defaultValue: "請先登入以回覆留言" })}
                  </div>
                ))}
              {/* 顯示回覆 */}
              <div className="replies pl-4 mt-2 space-y-1">
                {getReplies(c.replies, c.id).map((r) => (
                  <div key={r.id} className="flex items-start gap-2">
                    <Avatar
                      userId={r.user_id}
                      height={20}
                      width={20}
                      imageUrl={r.profilePicture}
                      name={r.username}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">
                          {r.is_anonymous
                            ? t("anonymous") || "匿名"
                            : r.username || t("user") || "用戶"}
                        </span>
                        {r.user_id === adOwnerId && (
                          <span className="text-xs bg-orange-200 text-orange-700 px-2 py-0.5 rounded">
                            {t("sellerReply")}
                          </span>
                        )}
                        {r.is_private === 1 && (
                          <span className="text-xs bg-blue-200 text-blue-700 px-2 py-0.5 rounded ml-1">
                            {t("pm")}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">
                          {/* 顯示香港時間 */}
                          {toHongKongTime(r.created_at)}
                        </span>
                      </div>
                      <div className="whitespace-pre-line break-words text-sm mb-1">
                        {r.is_private === 1 &&
                        !(
                          currentUser &&
                          (currentUser.id === adOwnerId ||
                            currentUser.id === r.user_id ||
                            currentUser.id === c.user_id)
                        )
                          ? t("pmHidden")
                          : r.content}
                      </div>
                      <div className="flex gap-3 items-center text-xs text-gray-500 mt-1">
                        <button
                          className={`flex items-center gap-1 ${
                            r.liked ? "text-orange-500" : ""
                          }`}
                          onClick={() => handleLike(r.id)}
                          disabled={!token || likeLoading[r.id] || isLoading}
                          title={r.liked ? t("liked") : t("like")}
                        >
                          👍 <span>{r.like_count || 0}</span>
                        </button>
                        <button
                          className={`flex items-center gap-1 ${
                            r.reported ? "text-red-500" : ""
                          }`}
                          onClick={() => handleReport(r.id)}
                          disabled={
                            !token ||
                            reportLoading[r.id] ||
                            r.reported ||
                            isLoading
                          }
                          title={r.reported ? t("reported") : t("report")}
                        >
                          🚩
                          <span>
                            {r.reported ? t("reported") : t("report")}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
        {mainComments.length === 0 && (
          <div className="text-gray-400">{t("noComments")}</div>
        )}
      </div>
    </div>
  );
}

export default AdComments;
